/**
 * Row-action icons and the button that wraps them.
 *
 * These paths were pasted inline at every call site — the pencil in four files, the trash
 * in five — so the stage list drifted into a text "Edit" next to an ✕ while every other
 * row on the same page used pencil + trash. Nothing was wrong with any single copy; there
 * was just no one place that said what an edit button looks like.
 *
 * Heroicons v1 outline paths, matching what was already in use. Kept as inline SVG rather
 * than adding @heroicons/react: the paths are already here, and a dependency for six
 * glyphs would be paid for by every build.
 */
import { ReactNode } from 'react';

interface IconProps {
  /** Tailwind size classes. Defaults to the 4×4 every row action already used. */
  className?: string;
}

const Svg = ({ className = 'w-4 h-4', children }: IconProps & { children: ReactNode }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const PencilIcon = (p: IconProps) => (
  <Svg {...p}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </Svg>
);

export const CogIcon = (p: IconProps) => (
  <Svg {...p}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </Svg>
);

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </Svg>
);

export const DuplicateIcon = (p: IconProps) => (
  <Svg {...p}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </Svg>
);

/**
 * Restores something to its inherited or default state.
 *
 * Distinct from {@link TrashIcon} on purpose: clearing a stage override destroys nothing —
 * the stage keeps running, on the model behind it. A trash can promises loss that does not
 * happen here, and the ✕ this replaces read as "dismiss".
 */
export const RevertIcon = (p: IconProps) => (
  <Svg {...p}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h10a5 5 0 010 10h-1M3 10l4-4M3 10l4 4"
    />
  </Svg>
);

type Tone = 'blue' | 'red' | 'green';

const TONES: Record<Tone, string> = {
  blue: 'hover:text-blue-600 dark:hover:text-blue-400',
  green: 'hover:text-green-600 dark:hover:text-green-400',
  red: 'hover:text-red-600 dark:hover:text-red-400',
};

interface RowActionProps {
  /** Required — an icon-only button is unlabelled without it, to a mouse and a reader. */
  title: string;
  onClick: () => void;
  children: ReactNode;
  tone?: Tone;
  disabled?: boolean;
  className?: string;
}

/**
 * The chrome shared by every icon-only row action: hit area, resting grey, hover tone.
 *
 * `title` doubles as `aria-label`. The buttons this replaces had neither on the icon
 * itself, so a stage's ✕ announced as an empty button.
 */
export const RowAction = ({
  title,
  onClick,
  children,
  tone = 'blue',
  disabled = false,
  className = '',
}: RowActionProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={`p-1.5 text-gray-400 dark:text-gray-500 ${TONES[tone]} disabled:opacity-30 disabled:hover:text-gray-400 dark:disabled:hover:text-gray-500 ${className}`}
  >
    {children}
  </button>
);

/**
 * The busy circle, and the block that centres it in a region that has no content yet.
 *
 * Not `LoadingSpinner`: that one is 16×16 with a sparkle and a carousel of jokes, built
 * for a wait where an LLM is thinking and there is nothing to say for ten seconds. A list
 * fetch is a second, and a page that greets you with "Mining semantic gold..." reads as a
 * bigger event than it is.
 */
export const SpinnerIcon = ({ className = 'w-4 h-4' }: IconProps) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

/**
 * "This region is fetching" — the thing to render INSTEAD of an empty list.
 *
 * A first load with no rows yet is indistinguishable from a genuine empty result, and the
 * empty-state copy is usually suppressed while loading (correctly — it would be a lie), so
 * what is left on screen is a heading, some filters, and nothing. This is what goes there.
 */
export const LoadingRegion = ({
  label = 'Loading…',
  className = 'py-12',
}: {
  /** Say what is being fetched. The default is only right when nothing better fits. */
  label?: string;
  className?: string;
}) => (
  <div
    className={`flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 ${className}`}
    role="status"
  >
    <SpinnerIcon className="w-4 h-4 text-blue-500" />
    <span>{label}</span>
  </div>
);
